import { useQueryClient } from '@tanstack/react-query';
import { useTaskCreateQuery, useTaskUpdateQuery } from 'hooks/api';
import { useModal } from 'hooks/ui';
import { useMemo } from 'react';
import { useTaskStore, resetDraft } from 'store/task.store';

type HookArgs = {
  onSuccess: () => void;
  onAbort: () => void;
};

export const useTaskSwitcher = () => {
  const queryClient = useQueryClient();
  const modal = useModal();
  const draftTask = useTaskStore((state) => state.draft);
  const errors = useTaskStore((state) => state.errors);
  const { mutate: createTask } = useTaskCreateQuery();
  const { mutate: updateTask } = useTaskUpdateQuery();
  const isFormDraftable =
    draftTask?.validator === 'CONTAINS'
      ? !draftTask?.title.trim().length || !draftTask?.answer.length
      : !draftTask?.title.trim().length;
  const hasErrors = useMemo(
    () => Object.values(errors).some((field) => field.length > 0),
    [errors],
  );

  const promptToUpdate = ({ onSuccess, onAbort }: HookArgs): void =>
    modal.save({
      text: 'Do you want to save the changes? Otherwise the changes will be lost.',
      onSuccess: (): void => {
        updateTask({ id: draftTask.id, task: draftTask });
        onSuccess();
      },
      onAbort: (): void => {
        resetDraft();
        queryClient.refetchQueries(['tasks']);
        onAbort();
      },
      isDisabled: isFormDraftable || hasErrors,
    });

  const promptToCreate = ({ onSuccess, onAbort }: HookArgs): void =>
    modal.save({
      text: 'Do you want to create a new task? Otherwise the changes will be lost.',
      onSuccess: (): void => {
        createTask(draftTask);
        onSuccess();
      },
      onAbort: (): void => {
        resetDraft();
        queryClient.refetchQueries(['tasks']);
        onAbort();
      },
      isDisabled: isFormDraftable || hasErrors,
    });

  return ({ onSuccess, onAbort }: HookArgs) => {
    const isDraftDirty = draftTask?.status === 'NOT_SAVED';
    const isDraftNew = !draftTask?.id;
    const isDraftEmpty = Object.values(draftTask ?? {})
      .filter((value) => value && value !== 'NOT_SAVED' && value !== 'NULL')
      .every((value) => value.length === 0);

    if (isDraftDirty && !isDraftEmpty) {
      if (isDraftNew) {
        promptToCreate({ onSuccess, onAbort });
      } else {
        promptToUpdate({ onSuccess, onAbort });
      }
    } else {
      onSuccess();
    }
  };
};
